import { useCallback } from 'react';
import { useSelector } from 'react-redux';
import type { RootState } from '@/store';
import { attendanceService } from '@/api/domains/attendance';
import { useExport } from '@/hooks/ui/useExport';
import { useUI } from '@/hooks/ui/useUI';
import { useTranslation } from '@/i18n';
import type { UIExportFormat } from '@/types/fileExport.types';


export interface UseMyAttendanceExportParams {
  startDate: string;
  endDate: string;
  searchText?: string | null;
}

export interface UseMyAttendanceExportResult {
  exportAttendance: (format: UIExportFormat) => Promise<void>;
  isExporting: boolean;
}

export function useMyAttendanceExport(
  params: UseMyAttendanceExportParams,
): UseMyAttendanceExportResult {
  const { t: translate } = useTranslation('attendance');
  const { showNotification } = useUI();
  const employeeId = useSelector((state: RootState) => state.auth.user?.idEmpleado);

  const { onExport: exportFile, isExporting } = useExport({
    exportFn: () => attendanceService.exportEmployeeAttendance(
      employeeId!,
      params.startDate,
      params.endDate,
      params.searchText,
    ),
    successMessage: translate('messages.exportSuccess'),
    errorMessage: translate('messages.exportError'),
  });

  const exportAttendance = useCallback(async (format: UIExportFormat) => {
    if (format !== 'EXCEL') {
      showNotification('error', translate('messages.exportError'), 5000);
      return;
    }
    if (!employeeId) {
      showNotification('error', translate('messages.exportError'), 5000);
      return;
    }
    await exportFile();
  }, [exportFile, employeeId, showNotification, translate]);

  return { exportAttendance, isExporting };
}

export default useMyAttendanceExport;
